/**
 * ProtectedRoute.jsx
 * Route guard for React Router v6.
 *
 * Usage examples:
 *
 *   // Any authenticated user
 *   <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
 *
 *   // Require a specific permission
 *   <Route path="/admin" element={
 *     <ProtectedRoute permission="view_admin_panel"><AdminPanel /></ProtectedRoute>
 *   } />
 *
 *   // Require a specific role
 *   <Route path="/settings/credentials" element={
 *     <ProtectedRoute permission="manage_credentials"><CredentialManager /></ProtectedRoute>
 *   } />
 */

import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from './AuthContext';

export default function ProtectedRoute({ children, permission = null, role = null }) {
  const { isAuthenticated, loading, can, hasRole } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div style={styles.loadingWrapper}>
        <div style={styles.spinner} />
        <p style={styles.loadingText}>Verifying access...</p>
      </div>
    );
  }

  // Not logged in → send to login, remembering where they were headed
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Logged in but missing a required permission
  if (permission && !can(permission)) {
    return <AccessDenied reason={`Requires "${permission}" permission`} />;
  }

  // Logged in but wrong role
  if (role && !hasRole(role)) {
    return <AccessDenied reason={`Requires "${role}" role`} />;
  }

  return children;
}

function AccessDenied({ reason }) {
  return (
    <div style={styles.deniedWrapper}>
      <div style={styles.deniedCard}>
        <div style={styles.deniedIcon}>🔒</div>
        <h2 style={styles.deniedTitle}>Access Denied</h2>
        <p style={styles.deniedMessage}>
          You don't have permission to view this page.
        </p>
        <p style={styles.deniedReason}>{reason}</p>
        <p style={styles.deniedContact}>
          Contact your administrator if you need access.
        </p>
      </div>
    </div>
  );
}

const styles = {
  loadingWrapper: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100vh',
    background: '#1a1a2e',
    gap: '1rem',
  },
  spinner: {
    width: 40,
    height: 40,
    border: '3px solid #2c3e50',
    borderTop: '3px solid #3498db',
    borderRadius: '50%',
    animation: 'spin 0.8s linear infinite',
  },
  loadingText: {
    color: '#7f8c8d',
    fontSize: '0.9rem',
  },
  deniedWrapper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100vh',
    background: '#1a1a2e',
  },
  deniedCard: {
    background: '#16213e',
    border: '1px solid #2c3e50',
    borderRadius: 12,
    padding: '3rem',
    textAlign: 'center',
    maxWidth: 420,
  },
  deniedIcon: {
    fontSize: '3rem',
    marginBottom: '1rem',
  },
  deniedTitle: {
    color: '#e74c3c',
    fontSize: '1.5rem',
    fontWeight: 600,
    margin: '0 0 0.75rem',
  },
  deniedMessage: {
    color: '#bdc3c7',
    marginBottom: '0.5rem',
  },
  deniedReason: {
    color: '#7f8c8d',
    fontSize: '0.85rem',
    fontStyle: 'italic',
    marginBottom: '1.5rem',
  },
  deniedContact: {
    color: '#7f8c8d',
    fontSize: '0.85rem',
  },
};
